"use client"

import { useMemo, useState } from "react"
import { ArrowUpRight, Info, RefreshCw } from "lucide-react"
import {
  StatBorderGrid,
  ToolPageShell,
  ToolProductCarousel,
  ToolShareBar,
} from "@/components/brand-kit"
import { TOOL_METADATA } from "@/lib/tool-metadata"
import { readCount } from "@/lib/tools/numeric-input"

const FALLBACK_RELATED = [
  "freezer-btu-calculator",
  "freezer-energy-cost-calculator",
  "freezer-storage-capacity-calculator",
  "refrigerator-size-calculator",
]

function buildRelated(slugs: string[], currentSlug: string) {
  const seen = new Set<string>([currentSlug])
  const out: { title: string; category: string; description: string; href: string; time: string }[] = []
  for (const s of [...slugs, ...FALLBACK_RELATED]) {
    if (out.length === 4) break
    if (seen.has(s)) continue
    const m = TOOL_METADATA[s]
    if (!m) continue
    seen.add(s)
    out.push({
      title: m.name,
      category: m.category,
      description: m.shortDescription,
      href: `/tools/${s}`,
      time: "~30 sec",
    })
  }
  return out
}

const META = {
  name: "Garage Freezer Size Calculator",
  category: "Calculator" as const,
  mediumDescription:
    "Buying a freezer for your garage? Get a size range and a clear answer on whether you need a garage-rated unit. Sized using AHAM household-storage guidance and ENERGY STAR garage-ready freezer specifications.",
  faqs: [
    { question: "Do I need a garage-rated freezer for my garage?", answer: "Yes, if your garage temperature drops below ~32°F in winter or rises above ~100°F in summer. Standard freezers have thermostats that misbehave outside roughly 55–90°F ambient." },
    { question: "What's a garage-ready freezer?", answer: "Garage-ready models have an enhanced thermostat and a small heater that keeps the compressor cycling correctly between 0°F and 110°F ambient — well outside the standard freezer's operating range." },
    { question: "What size garage freezer does a family of 4 need?", answer: "Roughly 13–20 cu ft for general storage. Bulk buyers and hunters should plan on 20–32 cu ft to handle a quarter beef or annual game harvest." },
    { question: "Should I get chest or upright for the garage?", answer: "Chest freezers handle bulk storage, ambient swings, and outages better. Uprights are easier to organize. Most garage shoppers go chest unless space is tight." },
    { question: "Will a garage freezer cost more to run than indoor?", answer: "Yes — typically 20% more in summer because the compressor works against hotter ambient air. A garage-rated unit is built to handle this without breaking down." },
    { question: "Is it safe to put a freezer in an uninsulated garage?", answer: "Only with a garage-rated unit. Standard freezers can fail or even thaw their contents when winter ambient drops below 32°F because the thermostat assumes the room is warmer than the freezer interior." },
  ],
  relatedTools: ["freezer-btu-calculator", "freezer-energy-cost-calculator", "freezer-storage-capacity-calculator"],
}

export function GarageFreezerSizingPageClient() {
  const [householdSize, setHouseholdSize] = useState("4")
  const [garageTemp, setGarageTemp] = useState("moderate")
  const [storageNeeds, setStorageNeeds] = useState("general")
  const [hasInsulation, setHasInsulation] = useState(false)

  // Original calculation logic preserved verbatim from the legacy page-client.
  const result = useMemo(() => {
    const size = readCount(householdSize, { min: 1, max: 20 })
    if (size === null) return null

    let minCuFt = 5 + size * 2
    let maxCuFt = 8 + size * 3

    if (storageNeeds === "bulk-buying") {
      minCuFt *= 1.5
      maxCuFt *= 1.8
    } else if (storageNeeds === "hunting") {
      minCuFt *= 2
      maxCuFt *= 2.5
    } else if (storageNeeds === "meal-prep") {
      minCuFt *= 1.3
      maxCuFt *= 1.5
    }

    let freezerType = ""
    let temperatureRating = ""
    if (garageTemp === "extreme-cold" || garageTemp === "extreme-hot") {
      freezerType = "Garage-rated freezer required"
      temperatureRating = "0°F to 110°F operating range"
    } else if (garageTemp === "moderate" && hasInsulation) {
      freezerType = "Standard freezer acceptable"
      temperatureRating = "55°F to 90°F operating range"
    } else {
      freezerType = "Garage-rated freezer recommended"
      temperatureRating = "0°F to 110°F operating range"
    }

    const recommendedSize = `${minCuFt.toFixed(1)} – ${maxCuFt.toFixed(1)} cu ft`

    return {
      minCuFt: parseFloat(minCuFt.toFixed(1)),
      maxCuFt: parseFloat(maxCuFt.toFixed(1)),
      recommendedSize,
      freezerType,
      temperatureRating,
    }
  }, [householdSize, garageTemp, storageNeeds, hasInsulation])

  return (
    <ToolPageShell
      toolName={META.name}
      heroEyebrow={META.category}
      heroTitlePre="What size freezer for"
      heroTitleItalic="your"
      heroTitlePost="garage — and does it need to be rated?"
      heroSubtitle={META.mediumDescription}
      heroMeta={["⏱ ~25 sec", "AHAM sizing + ENERGY STAR garage specs"]}
      heroShare={<ToolShareBar title={META.name} />}
      heroRight={
        <StatBorderGrid
          stats={[
            { value: "0–110°F", label: "Garage-rated", note: "Operating range" },
            { value: "55–90°F", label: "Standard", note: "Operating range" },
            { value: "+20%", label: "Hot ambient", note: "Energy use" },
            { value: "2–3 cu ft", label: "Per person", note: "AHAM base" },
          ]}
          className="w-full max-w-md md:max-w-none md:w-auto mx-auto md:mx-0"
        />
      }
      faqs={META.faqs}
      relatedTools={buildRelated(META.relatedTools, "garage-freezer-size-calculator")}
      cta={{
        title: "Ready to spec a freezer your garage won't kill?",
        body: "Browse upright freezers — many models are garage-rated for 0°F to 110°F ambient temperatures.",
        buttonLabel: "Browse upright freezers",
        buttonHref: "/collections/upright-freezers",
      }}
    >
      <section className="bg-gray-50 py-8 md:py-12">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8 space-y-6">
          <div className="grid lg:grid-cols-2 gap-6 lg:items-stretch">
            <div className="bg-white rounded-xl p-6 md:p-7 border border-gray-900 flex flex-col">
              <div className="flex items-center gap-3 mb-2">
                <span className="font-mono text-[12px] font-bold tracking-[0.15em] text-[#ff9951]">STEP 01</span>
                <div className="flex-1 h-[1px] bg-gray-300" />
              </div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: 28, letterSpacing: "-0.025em", lineHeight: 1.05 }}>
                Family.
                <br />
                Garage. Use case.
              </h2>
              <p className="text-[14px] text-gray-600 mt-2 leading-[1.5]">
                Bulk buying or hunting doubles your space need. Extreme garage temps require garage-rated units.
              </p>

              <div className="mt-7 space-y-5">
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Household size
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      min={1}
                      max={20}
                      step="1"
                      value={householdSize}
                      onChange={(e) => setHouseholdSize(e.target.value)}
                      className="tool-input-clean w-full px-4 py-4 text-[22px] font-bold rounded-lg outline-none transition-colors border border-gray-300 focus:border-[#ff9951]"
                      style={{ fontVariantNumeric: "tabular-nums" }}
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[13px] font-mono text-gray-500">people</span>
                  </div>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Garage temperature
                  </label>
                  <select
                    value={garageTemp}
                    onChange={(e) => setGarageTemp(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="mild">Mild (50–70°F)</option>
                    <option value="moderate">Moderate (70–90°F)</option>
                    <option value="extreme-hot">Extreme hot (90–110°F+)</option>
                    <option value="extreme-cold">Extreme cold (below 32°F)</option>
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] font-bold uppercase tracking-[0.12em] text-gray-900 mb-2">
                    Storage purpose
                  </label>
                  <select
                    value={storageNeeds}
                    onChange={(e) => setStorageNeeds(e.target.value)}
                    className="tool-input-clean w-full px-4 py-4 text-[18px] font-bold rounded-lg outline-none border border-gray-300 focus:border-[#ff9951] bg-white"
                  >
                    <option value="general">General overflow storage</option>
                    <option value="meal-prep">Meal prep (×1.3 to 1.5)</option>
                    <option value="bulk-buying">Bulk buying (×1.5 to 1.8)</option>
                    <option value="hunting">Hunting / game (×2 to 2.5)</option>
                  </select>
                </div>
                <div>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={hasInsulation}
                      onChange={(e) => setHasInsulation(e.target.checked)}
                      className="w-5 h-5 accent-[#ff9951]"
                    />
                    <span className="text-[14px] font-bold text-gray-900">Garage is insulated</span>
                  </label>
                  <div className="text-[11px] text-gray-500 mt-1.5 flex items-center gap-1.5">
                    <Info className="w-3.5 h-3.5" /> Insulation buffers ambient swings — but extreme climates still need garage-rated.
                  </div>
                </div>

                <div className="pt-2 text-[11px] uppercase tracking-[0.12em] text-gray-500 font-bold flex items-center gap-1.5">
                  <RefreshCw className="w-3 h-3" /> Updates live as you type
                </div>
              </div>
            </div>

            <div className="flex flex-col">
              <div className="rounded-2xl p-6 md:p-8 relative overflow-hidden text-white flex-1" style={{ background: "linear-gradient(135deg, #0469f0 0%, #0053c4 100%)" }}>
                <div aria-hidden className="absolute top-0 right-0 w-[340px] h-[340px] rounded-full pointer-events-none" style={{ background: "rgba(255,153,81,0.35)", filter: "blur(60px)", transform: "translate(25%, -35%)" }} />
                <div className="relative">
                  <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-white/70 mb-8">
                    Recommended size range
                  </div>
                  <div className="font-black" style={{ fontSize: "clamp(46px, 6.5vw, 80px)", letterSpacing: "-0.045em", lineHeight: 0.9 }}>
                    {result?.recommendedSize ?? "—"}
                  </div>
                  <div className="mt-4 text-[14px] text-white/80 leading-[1.5]">
                    {result?.freezerType ?? "Enter household details to size your freezer."}
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:gap-3 mt-6 md:mt-8">
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Min size</div>
                      <div className="text-[18px] sm:text-[22px] font-black tracking-tight">{result?.minCuFt ?? "—"} cu ft</div>
                    </div>
                    <div className="rounded-lg p-3 sm:p-4 bg-white/10 border border-white/15">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/60 font-bold mb-1">Operating range</div>
                      <div className="text-[12px] sm:text-[14px] font-black tracking-tight">{result?.temperatureRating ?? "—"}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="Step 02 · Pick the unit"
          title="Upright freezers built for the garage"
          description="Many uprights are explicitly garage-rated — check spec sheets for ambient operating range before buying."
          collectionType="upright-freezer"
          fallbackCollectionType="chest-freezer"
        />
      </section>

      <section className="bg-gray-50 py-8 md:py-12 border-y border-gray-200">
        <div className="max-w-[1120px] mx-auto px-6 md:px-8">
          <div className="grid md:grid-cols-[1fr_1.3fr] gap-8 md:gap-12 items-start">
            <div>
              <div className="text-[11px] font-bold uppercase tracking-[0.15em] mb-3 text-[#ff9951]">Under the hood</div>
              <h2 className="text-gray-900 font-black" style={{ fontSize: "clamp(24px, 3vw, 36px)", letterSpacing: "-0.03em", lineHeight: 1.05 }}>
                AHAM sizing. <em className="italic text-[#ff9951]">Garage</em> reality.
              </h2>
              <p className="text-[14px] text-gray-600 mt-4 max-w-[400px] leading-[1.55]">
                Capacity baseline derived from AHAM household-freezer guidance; operating-range thresholds from ENERGY STAR garage-ready specifications and standard freezer manufacturer documentation.
              </p>
            </div>
            <ol className="list-none p-0 m-0 border-t border-gray-900">
              {[
                { n: "01", t: "Capacity baseline", d: "Min = 5 + (people × 2). Max = 8 + (people × 3). A 4-person household lands at roughly 13–20 cu ft for general use." },
                { n: "02", t: "Use-case multiplier", d: "Meal prep ×1.3–1.5. Bulk buying ×1.5–1.8. Hunting / game storage ×2–2.5. These dominate the sizing decision." },
                { n: "03", t: "Garage-rated check", d: "Standard freezers operate 55–90°F. If your garage drops below 32°F or hits above 100°F, you need a garage-rated unit (0–110°F operating range)." },
              ].map((s) => (
                <li key={s.n} className="grid grid-cols-[auto_1fr_auto] gap-4 sm:gap-7 items-center py-5 sm:py-6 border-b border-gray-900">
                  <div className="font-mono text-sm font-bold tracking-[0.05em] min-w-[28px] sm:min-w-[34px] text-[#ff9951]">{s.n}</div>
                  <div>
                    <h3 className="text-gray-900" style={{ fontSize: 22, fontWeight: 800, letterSpacing: "-0.02em" }}>{s.t}</h3>
                    <p className="mt-1.5 text-[14px] text-gray-600 leading-[1.55]">{s.d}</p>
                  </div>
                  <ArrowUpRight className="w-4 h-4 sm:w-5 sm:h-5 text-gray-500 flex-shrink-0" />
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      <section className="bg-white py-4 md:py-6">
        <ToolProductCarousel
          eyebrow="You might also like"
          title="Chest freezers for bulk and hunting"
          description="Densest packing, lowest energy use, longest cold-hold during outages — the bulk-storage standard."
          collectionType="chest-freezer"
          fallbackCollectionType="upright-freezer"
        />
      </section>
    </ToolPageShell>
  )
}
